// stores/dashboardActions.ts — dashboardStore action implementations (design §3, §9.3).
//
// Split out of dashboardStore.ts to keep both files < 250 LOC; pure helpers live
// in dashboardActions.helpers.ts. The store owns the persist/merge wrapper; this
// file owns the layout/grid/panel mutations.
//
// Invariants honored here:
// - Presets are read-only: any write to an `isPreset` layout transparently
//   clones it to a custom first (ensureWritable), so shipped presets stay
//   re-seedable via resetLayout (§3).
// - The `'__ACTIVE__'` sentinel is never collapsed into persisted config — an
//   incoming patch that carries it is kept verbatim; resolution is render-time
//   only (§9.3). updatePanelConfig therefore performs a plain shallow merge.
// - exactOptionalPropertyTypes: optional fields are omitted, never set to
//   `undefined`. setSyncGroup deletes the key when group === null.

import type { DashboardActions } from './dashboardStore.types';
import type {
  GridItem,
  PanelConfig,
  PanelType,
} from '../pages/chart/modular/dashboardTypes';
import { PRESET_LAYOUTS } from '../pages/chart/layouts/presets';
import { getPanelDef } from '../pages/chart/modular/panelRegistry';
import {
  cloneLayout,
  defaultsFor,
  ensureWritable,
  nextRow,
  patchLayout,
  shortId,
  type DashboardGet,
  type DashboardSet,
} from './dashboardActions.helpers';

const GRID_COLS = 12;

type Rect = { x: number; y: number; w: number; h: number };

/** True when two grid rectangles share any cell. */
function overlaps(a: Rect, b: GridItem): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Pick a non-overlapping slot for a freshly-added panel: walk the distinct row
 * bands top→bottom and return the leftmost free x in the first band where a
 * `w`-wide panel fits beside its neighbours — so it tiles BESIDE existing panels
 * rather than stacking on x:0. Falls back to (x:0, nextRow) if nothing fits.
 */
function placeNewPanel(grid: GridItem[], w: number, h: number): Rect {
  const bands = Array.from(new Set(grid.map((g) => g.y))).sort((p, q) => p - q);
  for (const y of bands) {
    for (let x = 0; x + w <= GRID_COLS; x += 1) {
      const candidate = { x, y, w, h };
      if (!grid.some((g) => overlaps(candidate, g))) return candidate;
    }
  }
  return { x: 0, y: nextRow(grid), w, h };
}

export function createDashboardActions(
  set: DashboardSet,
  get: DashboardGet,
): DashboardActions {
  return {
    setActiveLayout: (id) => {
      if (get().layouts[id]) set({ activeLayout: id });
    },

    updateGrid: (layoutId, grid) => {
      const writable = ensureWritable(set, get, layoutId);
      if (!writable) return;
      patchLayout(set, writable, (l) => ({ ...l, grid }));
    },

    addPanel: (layoutId, type, at) => {
      const writable = ensureWritable(set, get, layoutId);
      if (!writable) return '';
      const id = `panel-${shortId()}`;
      const { size, min, config } = defaultsFor(type);
      patchLayout(set, writable, (l) => {
        // Explicit drop point wins; otherwise auto-place beside existing panels
        // (first free x in the topmost band that fits) instead of stacking x:0.
        const spot = at ?? placeNewPanel(l.grid, size.w, size.h);
        const item: GridItem = {
          i: id,
          x: spot.x,
          y: spot.y,
          w: size.w,
          h: size.h,
          minW: min.minW,
          minH: min.minH,
        };
        const panel: PanelConfig = {
          type,
          title: getPanelDef(type)?.label ?? type,
          config,
        };
        return {
          ...l,
          grid: [...l.grid, item],
          panelConfigs: { ...l.panelConfigs, [id]: panel },
        };
      });
      return id;
    },

    removePanel: (layoutId, panelId) => {
      const writable = ensureWritable(set, get, layoutId);
      if (!writable) return;
      patchLayout(set, writable, (l) => {
        const { [panelId]: _removed, ...rest } = l.panelConfigs;
        return {
          ...l,
          grid: l.grid.filter((g) => g.i !== panelId),
          panelConfigs: rest,
        };
      });
    },

    duplicatePanel: (layoutId, panelId) => {
      const writable = ensureWritable(set, get, layoutId);
      if (!writable) return '';
      const layout = get().layouts[writable];
      const source = layout?.panelConfigs[panelId];
      const sourceGrid = layout?.grid.find((g) => g.i === panelId);
      if (!layout || !source || !sourceGrid) return '';
      const id = `panel-${shortId()}`;
      patchLayout(set, writable, (l) => {
        const item: GridItem = {
          ...structuredClone(sourceGrid),
          i: id,
          y: nextRow(l.grid),
        };
        return {
          ...l,
          grid: [...l.grid, item],
          panelConfigs: {
            ...l.panelConfigs,
            [id]: structuredClone(source) as PanelConfig,
          },
        };
      });
      return id;
    },

    updatePanelConfig: (layoutId, panelId, patch) => {
      const writable = ensureWritable(set, get, layoutId);
      if (!writable) return;
      patchLayout(set, writable, (l) => {
        const panel = l.panelConfigs[panelId];
        if (!panel) return l;
        // Sentinel-safe (§9.3): a plain shallow merge keeps '__ACTIVE__' if the
        // patch carries it; resolution stays render-time only.
        return {
          ...l,
          panelConfigs: {
            ...l.panelConfigs,
            [panelId]: { ...panel, config: { ...panel.config, ...patch } },
          },
        };
      });
    },

    updatePanelConfigs: (layoutId, patches) => {
      // Resolve the writable layout ONCE: a preset is cloned to a custom a single
      // time (and activeLayout flips to that clone once), then EVERY patch is merged
      // into that same resolved layout. Looping updatePanelConfig instead would re-run
      // ensureWritable per panel, re-cloning the still-preset source each iteration so
      // each panel landed in its OWN fresh clone and activeLayout ended on the last one
      // (only the last-iterated panel updated) — the watchlist symbol-sync bug.
      const writable = ensureWritable(set, get, layoutId);
      if (!writable) return;
      patchLayout(set, writable, (l) => {
        let panelConfigs = l.panelConfigs;
        let mutated = false;
        for (const [panelId, patch] of Object.entries(patches)) {
          const panel = panelConfigs[panelId];
          if (!panel) continue; // skip panels not in this layout
          if (!mutated) {
            panelConfigs = { ...panelConfigs };
            mutated = true;
          }
          // Sentinel-safe shallow merge, same as updatePanelConfig (§9.3).
          panelConfigs[panelId] = { ...panel, config: { ...panel.config, ...patch } };
        }
        return mutated ? { ...l, panelConfigs } : l;
      });
    },

    setPanelType: (layoutId, panelId, type) => {
      const writable = ensureWritable(set, get, layoutId);
      if (!writable) return;
      const { config } = defaultsFor(type);
      patchLayout(set, writable, (l) => {
        const panel = l.panelConfigs[panelId];
        if (!panel) return l;
        const next: PanelConfig = {
          type,
          title: getPanelDef(type)?.label ?? type,
          config,
        };
        return {
          ...l,
          panelConfigs: { ...l.panelConfigs, [panelId]: next },
        };
      });
    },

    cloneAsCustom: (sourceId, name) => {
      const source = get().layouts[sourceId];
      if (!source) return '';
      const custom = cloneLayout(source, name);
      set((s) => ({
        layouts: { ...s.layouts, [custom.id]: custom },
        activeLayout: custom.id,
      }));
      return custom.id;
    },

    renameLayout: (layoutId, name) => {
      const layout = get().layouts[layoutId];
      if (!layout || layout.isPreset) return; // presets are read-only
      patchLayout(set, layoutId, (l) => ({ ...l, name }));
    },

    deleteLayout: (layoutId) => {
      const layout = get().layouts[layoutId];
      if (!layout || layout.isPreset) return; // never delete a preset
      set((s) => {
        const { [layoutId]: _gone, ...rest } = s.layouts;
        const activeLayout =
          s.activeLayout === layoutId ? 'trading' : s.activeLayout;
        return { layouts: rest, activeLayout };
      });
    },

    resetLayout: (layoutId) => {
      // Re-seed from the shipped preset of the same id (custom layouts have no
      // preset source → no-op).
      const preset = PRESET_LAYOUTS[layoutId];
      if (!preset) return;
      set((s) => ({
        layouts: { ...s.layouts, [layoutId]: structuredClone(preset) },
      }));
    },

    setSyncGroup: (layoutId, panelId, group) => {
      const writable = ensureWritable(set, get, layoutId);
      if (!writable) return;
      patchLayout(set, writable, (l) => {
        const panel = l.panelConfigs[panelId];
        if (!panel) return l;
        const cfg = { ...(panel.config as Record<string, unknown>) };
        if (group === null) delete cfg.syncGroup;
        else cfg.syncGroup = group;
        return {
          ...l,
          panelConfigs: {
            ...l.panelConfigs,
            [panelId]: { ...panel, config: cfg as PanelConfig['config'] },
          },
        };
      });
    },

    setLockLayout: (locked) => set({ lockLayout: locked }),

    toggleLockLayout: () => set((s) => ({ lockLayout: !s.lockLayout })),
  };
}
